import { ICreateRoomStateEvent, Preset, Visibility } from 'matrix-js-sdk/src/matrix';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { DEFAULT_BOT_POWER_LEVEL } from 'modules/CreateWorkspaceModal/WorkspaceCreateStep/utils';
import { buildRoomPowerLevelOverrides, MEEDIO_BOT_ID, RoomPowerLevelOverridesOptions } from 'utils/matrixUtils';

type CreateRoomProps = {
  title: string;
  alias?: string;
  description?: string;
  customInitialStates?: ICreateRoomStateEvent[];
  customCreationContent?: object;
  preset?: Preset;
  powerLevelOverrides?: RoomPowerLevelOverridesOptions;
  includeBot?: boolean;
  ownerPowerLevel?: number;
};

const useCreateRoom = () => {
  const { matrixClient } = useMatrixContext();

  const createRoom = async ({
    title,
    alias,
    description,
    customInitialStates,
    customCreationContent,
    preset = Preset.PublicChat,
    powerLevelOverrides,
    includeBot = false,
    ownerPowerLevel = 100,
  }: CreateRoomProps) => {
    const userId = matrixClient.getUserId();
    const initialState = customInitialStates || [];

    const usersPowerLevels: Record<string, number> = {};
    if (userId) usersPowerLevels[userId] = ownerPowerLevel;
    if (includeBot && MEEDIO_BOT_ID) usersPowerLevels[MEEDIO_BOT_ID] = DEFAULT_BOT_POWER_LEVEL;

    const invite = includeBot && MEEDIO_BOT_ID ? [MEEDIO_BOT_ID] : undefined;

    return await matrixClient.createRoom({
      visibility: Visibility.Private,
      preset,
      room_version: '11', // latest stable room version. https://spec.matrix.org/v1.12/rooms/
      initial_state: initialState,
      creation_content: customCreationContent,
      topic: description,
      name: title,
      power_level_content_override: buildRoomPowerLevelOverrides(userId, powerLevelOverrides, usersPowerLevels),
      ...(alias && { room_alias_name: alias }),
      invite,
    });
  };

  return { createRoom };
};

export default useCreateRoom;
